import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrder } from '../../context/OrderContext';
import { Header } from '../../layout';
import RALModal from '../../modals/RALModal';
import { api } from '../../services/api';
import {
  PENSILINE_TYPES, CARTER_OPTIONS, LENGTH_OPTIONS, FIXING_OPTIONS, FINISH_OPTIONS,
  composePensilineCode, getTappiCodes, getTappiPrice, ANTISFILAMENTO, getAntisfilamentoQty, getFinishTier,
} from '../../utils/pensilineFlowRules';
import Swal from 'sweetalert2';

const STANDARD_LENGTHS = [1500, 2000, 3000, 4000, 6000];

const PensilineOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useOrder();

  const [step, setStep] = useState('type');
  const [penType, setPenType] = useState(null);
  const [carter, setCarter] = useState(null);
  const [fixing, setFixing] = useState(null);
  const [glass, setGlass] = useState(null);
  const [finish, setFinish] = useState(null);
  const [ralCode, setRalCode] = useState('');
  const [glassWidth, setGlassWidth] = useState('');
  const [showRalModal, setShowRalModal] = useState(false);
  const [pendingFinish, setPendingFinish] = useState(null);
  const [supplyType, setSupplyType] = useState(null); // 'kit' or 'progettazione'

  // Kit groups
  const [kitGroups, setKitGroups] = useState([]);
  const [customRows, setCustomRows] = useState([]);

  // Accessories editable quantities & metadata
  const [tappiDxQty, setTappiDxQty] = useState(0);
  const [tappiSxQty, setTappiSxQty] = useState(0);
  const [antisfilQty, setAntisfilQty] = useState(0);
  const [accMeta, setAccMeta] = useState({}); // { code: { description, unit, price } }
  const [accLoading, setAccLoading] = useState(false);

  // Progettazione
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [detailModal, setDetailModal] = useState({ open: false, field: '', label: '' });
  const [progData, setProgData] = useState({
    metriIndicativi: '',
    vuoiGiunzioni: false, giunzioniDettaglio: '',
    vuoiCorrimano: false, corrimanoDettaglio: '',
    vuoiTappi: false,
    vuoiSpessori: false,
    vuoiCoprifilo: false, coprifiloDettaglio: '',
    vuoiLed: false,
  });

  const typeConfig = PENSILINE_TYPES.find(t => t.id === penType);
  const totalKits = kitGroups.reduce((s, g) => s + g.quantity, 0);

  const handleBack = () => {
    switch (step) {
      case 'type': navigate('/product-categories'); break;
      case 'carter': setStep('type'); break;
      case 'fixing': setStep('carter'); break;
      case 'glass': setStep('fixing'); break;
      case 'finish': setStep(penType === 'linea-mini' ? 'fixing' : 'glass'); break;
      case 'supplyType': setStep('finish'); break;
      case 'length': setStep('supplyType'); break;
      case 'accessories': setStep('length'); break;
      case 'upload': setStep('supplyType'); break;
      case 'notes': setStep('upload'); break;
      default: navigate('/product-categories');
    }
  };

  const updateProgData = (data) => setProgData(prev => ({ ...prev, ...data }));

  // --- Kit standard helpers ---
  const addStandardGroup = (mm) => {
    const existing = kitGroups.find(g => g.length === mm && !g.isCustom);
    if (existing) {
      setKitGroups(prev => prev.map(g => g.length === mm && !g.isCustom ? { ...g, quantity: g.quantity + 1 } : g));
    } else {
      setKitGroups(prev => [...prev, { quantity: 1, length: mm, isCustom: false }]);
    }
  };
  const removeGroup = (index) => setKitGroups(prev => prev.filter((_, i) => i !== index));
  const updateGroupQty = (index, qty) => {
    setKitGroups(prev => prev.map((g, i) => i === index ? { ...g, quantity: qty === '' ? 0 : Math.max(0, parseInt(qty) || 0) } : g));
  };
  const addCustomRow = () => setCustomRows(prev => [...prev, { quantity: 1, length: 0 }]);
  const removeCustomRow = (index) => setCustomRows(prev => prev.filter((_, i) => i !== index));
  const confirmCustomRows = () => {
    const valid = customRows.filter(r => r.quantity > 0 && r.length >= 200 && r.length <= 6000);
    const invalid = valid.filter(r => STANDARD_LENGTHS.includes(r.length));
    if (invalid.length > 0) {
      Swal.fire({ icon: 'error', title: 'Lunghezza non valida', text: `Le misure ${invalid.map(r => r.length + 'mm').join(', ')} sono kit standard. Usa i pulsanti sopra.`, confirmButtonColor: '#cd212a' });
      return;
    }
    if (valid.length === 0) return;
    valid.forEach(row => setKitGroups(prev => [...prev, { quantity: row.quantity, length: row.length, isCustom: true }]));
    setCustomRows([]);
  };

  // --- Add kit to cart ---
  const handleAddKitToCart = () => {
    if (kitGroups.length === 0) return;
    const sessionId = `pen_${Date.now()}`;

    // Resolve VS → V1/V2/V3 based on total meters
    let resolvedFinish = finish;
    if (finish === 'VS') {
      const totalMm = kitGroups.reduce((sum, g) => sum + g.quantity * g.length, 0);
      const totalMeters = totalMm / 1000;
      resolvedFinish = getFinishTier(totalMeters);
    }

    kitGroups.forEach(group => {
      const lengthCode = group.isCustom ? 'T' : LENGTH_OPTIONS.find(l => l.mm === group.length)?.code || String(group.length / 10);
      const code = composePensilineCode(penType, carter, lengthCode, fixing, glass, resolvedFinish);
      dispatch({
        type: 'ADD_TO_CART',
        payload: {
          productCode: code, quantity: group.quantity, category: 'pensiline', pensilineType: penType,
          description: `${typeConfig?.name} - ${CARTER_OPTIONS.find(c => c.code === carter)?.label} - ${group.length}mm`,
          isPensilineKit: true, isStandaloneArticle: true, ralCode: ralCode || null,
          pensilineCustomMm: group.isCustom ? group.length : null, profileSessionId: sessionId,
        },
      });
    });

    const tappiCodes = getTappiCodes(penType, carter, resolvedFinish);
    [{ code: tappiCodes.dx, side: 'DX', qty: tappiDxQty }, { code: tappiCodes.sx, side: 'SX', qty: tappiSxQty }].forEach(({ code, side, qty }) => {
      if (qty > 0) {
        const meta = accMeta[code];
        dispatch({
          type: 'ADD_TO_CART',
          payload: {
            productCode: code, quantity: qty, price: meta?.price || 0, category: 'pensiline',
            description: meta?.description || `Tappo ${side}`, unit: meta?.unit || 'PZ',
            isPensilineAccessory: true, isStandaloneArticle: true,
            isAutomaticAddition: true, profileSessionId: sessionId,
          },
        });
      }
    });

    const asConfig = ANTISFILAMENTO[penType];
    if (asConfig && antisfilQty > 0) {
      const meta = accMeta[asConfig.code];
      dispatch({
        type: 'ADD_TO_CART',
        payload: {
          productCode: asConfig.code, quantity: antisfilQty, price: meta?.price || asConfig.price, category: 'pensiline',
          description: meta?.description || 'Kit Antisfilamento', unit: meta?.unit || 'PZ',
          isPensilineAccessory: true, isStandaloneArticle: true,
          isAutomaticAddition: true, profileSessionId: sessionId,
        },
      });
    }

    Swal.fire({
      title: 'Kit Linea 3 aggiunto al carrello',
      html: `<strong>${totalKits} kit</strong>${tappiDxQty + tappiSxQty > 0 ? ` + ${tappiDxQty + tappiSxQty} tappi` : ''}${antisfilQty > 0 ? ` + ${antisfilQty} antisfilamento` : ''}`,
      icon: 'success', showCancelButton: true, confirmButtonText: 'Vai al carrello',
      cancelButtonText: 'Aggiungi altro', confirmButtonColor: '#cd212a',
    }).then((result) => {
      if (result.isConfirmed) navigate('/order-summary');
      else resetAll();
    });
  };

  // --- Add progettazione to cart ---
  const handleAddProgToCart = async () => {
    if (photos.length === 0) return;
    const sessionId = `pen_prog_${Date.now()}`;

    let uploadedFiles = [];
    try {
      const uploadResult = await api.uploadProjectFiles(photos);
      if (uploadResult.status === 'success') {
        uploadedFiles = uploadResult.uploaded_files;
      } else {
        Swal.fire({ icon: 'error', title: 'Errore', text: 'Errore nel caricamento dei file', confirmButtonColor: '#cd212a' });
        return;
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Errore', text: 'Errore nel caricamento dei file', confirmButtonColor: '#cd212a' });
      return;
    }

    let progFinish = finish;
    if (finish === 'VS') {
      const mt = parseFloat(progData.metriIndicativi) || 0;
      progFinish = mt > 0 ? getFinishTier(mt) : 'V3';
    }
    const code = composePensilineCode(penType, carter, 'T', fixing, glass, progFinish);

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        productCode: code, quantity: 1, category: 'pensiline', pensilineType: penType,
        description: `${typeConfig?.name} - Progettazione`,
        isPensilineKit: true, isPensilineProgettazione: true, isStandaloneArticle: true,
        ralCode: ralCode || null, profileSessionId: sessionId,
        processing: {
          supplyType: 'progettazione',
          photos: photos, uploadedFiles: uploadedFiles, notes: notes,
          metriIndicativi: progData.metriIndicativi,
          vuoiGiunzioni: progData.vuoiGiunzioni, giunzioniDettaglio: progData.giunzioniDettaglio,
          vuoiCorrimano: progData.vuoiCorrimano, corrimanoDettaglio: progData.corrimanoDettaglio,
          vuoiTappi: progData.vuoiTappi, vuoiSpessori: progData.vuoiSpessori,
          vuoiCoprifilo: progData.vuoiCoprifilo, coprifiloDettaglio: progData.coprifiloDettaglio,
          vuoiLed: progData.vuoiLed,
        },
      },
    });

    Swal.fire({
      title: 'Progettazione aggiunta al carrello',
      html: `<strong>${code}</strong> con ${uploadedFiles.length} file`,
      icon: 'success', showCancelButton: true, confirmButtonText: 'Vai al carrello',
      cancelButtonText: 'Aggiungi altro', confirmButtonColor: '#cd212a',
    }).then((result) => {
      if (result.isConfirmed) navigate('/order-summary');
      else resetAll();
    });
  };

  const resetAll = () => {
    setStep('type'); setPenType(null); setCarter(null); setFixing(null);
    setGlass(null); setFinish(null); setRalCode(''); setGlassWidth('');
    setKitGroups([]); setCustomRows([]); setSupplyType(null);
    setPhotos([]); setNotes('');
    setProgData({ metriIndicativi: '', vuoiGiunzioni: false, giunzioniDettaglio: '', vuoiCorrimano: false, corrimanoDettaglio: '', vuoiTappi: false, vuoiSpessori: false, vuoiCoprifilo: false, coprifiloDettaglio: '', vuoiLed: false });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title={location.state?.type === 'offerta' ? 'Richiesta Offerta' : 'Nuovo Ordine'}
        backText="Categoria" backTo="/product-categories"
      />
      <div className="container mx-auto px-4 py-6">
        {step !== 'type' && (
          <button onClick={handleBack} className="mb-4 px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Indietro
          </button>
        )}

        {/* ── Type ── */}
        {step === 'type' && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-4">SELEZIONA TIPOLOGIA LINEA 3</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {PENSILINE_TYPES.map(t => (
                <button key={t.id}
                  onClick={() => {
                    if (t.id === 'sfera-n') { navigate('/order-sferan', { state: location.state }); return; }
                    setPenType(t.id); setCarter(null); setFixing(null); setGlass(null); setFinish(null);
                    setKitGroups([]); setCustomRows([]); setSupplyType(null);
                    setStep('carter');
                  }}
                  className="p-4 border rounded-lg border-gray-300 hover:bg-red-100 hover:border-red-500 transition-colors duration-200"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-full h-20 mb-2 flex items-center justify-center">
                      <img
                        src={t.id === 'linea-mini' ? '/images/macrocagetorie/Linea-Mini-3.png' : t.id === 'linea-maxi' ? '/images/macrocagetorie/Linea-Maxi-3.png' : '/images/macrocagetorie/Sfera-N_singolo.png'}
                        alt={t.name}
                        className="h-16 object-contain"
                      />
                    </div>
                    <span className="font-medium">{t.name}</span>
                    <span className="text-sm text-gray-500 mt-1">{t.description}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Carter ── */}
        {step === 'carter' && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-4">TIPOLOGIA CARTER</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {CARTER_OPTIONS.map(c => (
                <button key={c.code} onClick={() => { setCarter(c.code); setStep('fixing'); }}
                  className="p-4 border rounded-lg border-gray-300 hover:bg-red-100 hover:border-red-500 transition-colors duration-200">
                  <div className="flex flex-col items-center">
                    <span className="font-medium">{c.label}</span>
                    <span className="text-sm text-gray-500 mt-1">{c.code === 'KL' ? 'Predisposto per illuminazione LED' : 'Carter standard a scatto'}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Fixing ── */}
        {step === 'fixing' && (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-4">TIPOLOGIA FISSAGGIO</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {FIXING_OPTIONS.map(f => (
                <button key={f.code}
                  onClick={() => {
                    setFixing(f.suffix);
                    if (penType === 'linea-mini') { setGlass('16'); setStep('finish'); }
                    else setStep('glass');
                  }}
                  className="p-4 border rounded-lg border-gray-300 hover:bg-red-100 hover:border-red-500 transition-colors duration-200">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 flex items-center justify-center mb-2 bg-gray-200 rounded-full">
                      <span className="text-2xl font-bold">{f.suffix || '—'}</span>
                    </div>
                    <span className="font-medium">{f.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Glass ── */}
        {step === 'glass' && typeConfig && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium mb-4">Seleziona spessore vetro</h3>
            <div className="grid grid-cols-2 gap-4">
              {(typeConfig.glassOptions || []).map(g => (
                <button key={g.code} onClick={() => { setGlass(g.code); setStep('finish'); }}
                  className={`p-4 rounded-lg border-2 transition-all ${glass === g.code ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                  <div className="text-center"><span className="text-xl font-medium">{g.label}</span></div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Finish ── */}
        {step === 'finish' && (() => {
          const standardFinishes = FINISH_OPTIONS.filter(f => f.group === 'standard');
          const hasSpecial = FINISH_OPTIONS.some(f => f.group === 'special');
          return (
            <div className="text-center">
              <h2 className="text-xl font-bold mb-4">Seleziona la Finitura</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                {standardFinishes.map(f => (
                  <button key={f.code}
                    onClick={() => { setFinish(f.code); setRalCode(''); setStep('supplyType'); }}
                    className={`p-4 border rounded-lg ${finish === f.code ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full mb-2 border border-gray-300" style={{ backgroundColor: f.color }} />
                      <span className="font-medium">{f.label}</span>
                      <span className="text-sm text-gray-500">({f.code})</span>
                    </div>
                  </button>
                ))}
                {hasSpecial && (
                  <button
                    onClick={() => { setPendingFinish('VS'); setShowRalModal(true); }}
                    className={`p-4 border rounded-lg ${finish === 'VS' ? 'bg-red-100 border-red-500' : 'border-gray-300'}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full mb-2 border border-gray-300" style={{ backgroundColor: '#D3D3D3' }} />
                      <span className="font-medium">Verniciato Speciale</span>
                      <span className="text-sm text-gray-500">(RAL a scelta)</span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Supply Type (like Ninfa SupplyTypeSelector) ── */}
        {step === 'supplyType' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-center mb-6">SEZIONE LAVORAZIONI</h2>
            <div className="space-y-4">
              <button
                onClick={() => { setSupplyType('kit'); setStep('length'); }}
                className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors duration-200 text-center"
              >
                <h3 className="text-lg font-medium mb-2">Fornitura Kit e A Misura</h3>
                <p className="text-gray-600 text-sm">Seleziona kit standard o inserisci misure personalizzate</p>
              </button>
              <button
                onClick={() => { setSupplyType('progettazione'); setStep('upload'); }}
                className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors duration-200 text-center"
              >
                <h3 className="text-lg font-medium mb-2">Fornitura con Progettazione</h3>
                <p className="text-gray-600 text-sm">Carica file di progetto per una valutazione personalizzata</p>
              </button>
            </div>
          </div>
        )}

        {/* ── Length (kit flow) ── */}
        {step === 'length' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-center mb-2">LUNGHEZZA KIT</h2>
            <p className="text-gray-600 text-center mb-6">Seleziona i kit standard e/o aggiungi misure personalizzate</p>
            <div className="space-y-6">
              {/* Configured groups */}
              {kitGroups.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Kit configurati:</p>
                  {kitGroups.map((group, i) => (
                    <div key={i} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-3">
                        <input type="number" inputMode="numeric" min="1" max="100"
                          value={group.quantity === 0 ? '' : group.quantity}
                          onChange={(e) => updateGroupQty(i, e.target.value)}
                          className="w-16 p-2 text-center text-base border border-green-300 rounded-lg focus:ring-2 focus:ring-red-500" />
                        <span className="text-green-800">
                          {group.isCustom ? 'su misura' : 'kit'} da <strong>{group.length}mm</strong>
                          {!group.isCustom && <span className="text-green-600 text-sm ml-1">({group.length / 10} cm)</span>}
                        </span>
                      </div>
                      <button onClick={() => removeGroup(i)} className="text-red-400 hover:text-red-600 text-lg font-bold px-2">✕</button>
                    </div>
                  ))}
                  <div className="text-sm text-gray-600 font-medium text-right">Totale: {totalKits} kit</div>
                </div>
              )}

              {/* Standard length buttons */}
              <div>
                <p className="text-sm font-medium text-gray-700 text-center mb-3">
                  {kitGroups.length > 0 ? 'Aggiungi kit standard:' : 'Seleziona lunghezza kit:'}
                </p>
                <div className="flex justify-center space-x-3 flex-wrap gap-y-3">
                  {STANDARD_LENGTHS.map(mm => (
                    <button key={mm} onClick={() => addStandardGroup(mm)}
                      className="p-4 border-2 rounded-lg text-center border-gray-200 hover:border-red-500 hover:bg-red-50 transition-all min-w-[90px]">
                      <div className="text-sm font-bold">{mm}</div>
                      <div className="text-xs text-gray-500">{mm / 10} cm</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom length rows */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Oppure aggiungi misure personalizzate:</p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <h4 className="font-medium text-yellow-900">Restrizioni</h4>
                  </div>
                  <p className="text-sm text-yellow-800">Lunghezza minima 200mm, massima 6000mm. Le misure standard (1500, 2000, 3000, 4000, 6000) vanno selezionate con i pulsanti sopra.</p>
                </div>

                <div className="space-y-3">
                  {customRows.map((row, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-1">
                        <input type="number" inputMode="numeric" min="1" max="100"
                          value={row.quantity === 0 ? '' : row.quantity}
                          onChange={(e) => { const newRows = [...customRows]; newRows[index].quantity = e.target.value === '' ? 0 : (parseInt(e.target.value) || 0); setCustomRows(newRows); }}
                          className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="N° kit" />
                      </div>
                      <span className="text-gray-500 font-medium">kit da</span>
                      <div className="flex-1">
                        <input type="number" inputMode="numeric" min="200" max="6000"
                          value={row.length === 0 ? '' : row.length}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { const newRows = [...customRows]; newRows[index].length = 0; setCustomRows(newRows); return; }
                            const v = parseInt(raw) || 0;
                            if (v > 6000 || STANDARD_LENGTHS.includes(v)) {
                              e.target.classList.add('border-red-500', 'bg-red-50');
                              setTimeout(() => { e.target.classList.remove('border-red-500', 'bg-red-50'); }, 1000);
                              return;
                            }
                            const newRows = [...customRows]; newRows[index].length = v; setCustomRows(newRows);
                          }}
                          className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="mm (200-6000)" />
                      </div>
                      <span className="text-gray-500 font-medium">mm</span>
                      <button onClick={() => removeCustomRow(index)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 mt-3">
                  <button onClick={addCustomRow}
                    className="flex-1 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-400 hover:text-red-600 transition-colors flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Aggiungi misura personalizzata
                  </button>
                  {customRows.length > 0 && (
                    <button onClick={confirmCustomRows} className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors">Conferma</button>
                  )}
                </div>
              </div>

              {/* Summary */}
              {kitGroups.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Riepilogo:</h4>
                  <div className="space-y-1 text-sm text-gray-700">
                    {kitGroups.map((g, idx) => (<div key={idx}>• {g.quantity} kit da {g.length}mm {g.isCustom ? '(su misura)' : `(${g.length / 10} cm)`}</div>))}
                    <div className="pt-2 mt-2 border-t border-gray-300 font-medium">Totale: {totalKits} kit</div>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button onClick={() => setStep('supplyType')} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">Indietro</button>
                <button
                  onClick={() => {
                    if (kitGroups.length === 0) { Swal.fire({ icon: 'warning', title: 'Nessun kit', text: 'Aggiungi almeno un kit', confirmButtonColor: '#cd212a' }); return; }
                    const validGroups = kitGroups.filter(g => g.quantity > 0);
                    if (validGroups.length === 0) { Swal.fire({ icon: 'warning', title: 'Nessun kit', text: 'Inserisci una quantità valida', confirmButtonColor: '#cd212a' }); return; }
                    // Validate custom min 200mm
                    const tooShort = validGroups.filter(g => g.isCustom && g.length < 200);
                    if (tooShort.length > 0) { Swal.fire({ icon: 'error', title: 'Errore', text: 'La lunghezza minima è 200mm', confirmButtonColor: '#cd212a' }); return; }
                    setKitGroups(validGroups);
                    // Set default quantities for accessories
                    const kits = validGroups.reduce((s, g) => s + g.quantity, 0);
                    setTappiDxQty(kits);
                    setTappiSxQty(kits);
                    setAntisfilQty(2 * kits);
                    // Fetch article metadata
                    const resolvedF = finish === 'VS' ? getFinishTier(validGroups.reduce((sum, g) => sum + g.quantity * g.length, 0) / 1000) : finish;
                    const tappi = getTappiCodes(penType, carter, resolvedF);
                    const asCode = ANTISFILAMENTO[penType]?.code;
                    const codes = [tappi.dx, tappi.sx, asCode].filter(Boolean);
                    setAccLoading(true);
                    Promise.all(codes.map(async (code) => {
                      try {
                        const res = await api.getArticleById(code);
                        if (res?.article) return [code, { description: res.article.Description || '', unit: res.article.PriceListUoM || 'PZ', price: parseFloat(res.article.Price) || 0 }];
                      } catch (e) { console.warn(`Failed to fetch ${code}`, e); }
                      return null;
                    })).then(results => {
                      const meta = {};
                      results.filter(Boolean).forEach(([code, data]) => { meta[code] = data; });
                      setAccMeta(meta);
                      setAccLoading(false);
                    });
                    setStep('accessories');
                  }}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50" disabled={kitGroups.length === 0}
                >Avanti</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Accessories (kit flow) ── */}
        {step === 'accessories' && (() => {
          const resolvedF = finish === 'VS' ? getFinishTier(kitGroups.reduce((sum, g) => sum + g.quantity * g.length, 0) / 1000) : finish;
          const tappi = getTappiCodes(penType, carter, resolvedF);
          const asCode = ANTISFILAMENTO[penType]?.code;
          const dxMeta = accMeta[tappi.dx];
          const sxMeta = accMeta[tappi.sx];
          const asMeta = accMeta[asCode];

          return (
          <div className="text-center">
            <h1 className="text-xl font-bold mb-4">ACCESSORI</h1>
            <p className="mb-6 text-gray-600">Verifica e modifica le quantità degli accessori</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
              <h4 className="font-medium text-red-900 mb-2">Riepilogo Kit</h4>
              {kitGroups.map((g, i) => (<p key={i} className="text-sm text-red-800">{g.quantity}× {g.isCustom ? `su misura ${g.length}mm` : `${g.length}mm`} — {CARTER_OPTIONS.find(c => c.code === carter)?.label}</p>))}
              {ralCode && <span className="inline-block mt-2 text-xs text-red-600 font-medium">RAL {ralCode}</span>}
            </div>

            {accLoading ? (
              <div className="flex items-center justify-center py-8 gap-3">
                <div className="w-8 h-8 border-[3px] border-gray-200 border-t-red-500 rounded-full animate-spin" />
                <p className="text-sm text-gray-400">Caricamento prezzi...</p>
              </div>
            ) : (
            <div className="space-y-4 text-left mb-6">
              {/* Tappo DX */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Consigliato</span>
                  <span className="font-medium">Tappo DX — <span className="font-mono text-sm text-gray-500">{tappi.dx}</span></span>
                </div>
                {dxMeta && <p className="text-sm text-gray-500 mb-2">{dxMeta.description}</p>}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Qtà:</label>
                    <input type="number" inputMode="numeric" min="0" value={tappiDxQty === 0 ? '' : tappiDxQty}
                      onChange={(e) => setTappiDxQty(e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-20 p-2 text-center text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
                    <span className="text-sm text-gray-500">{dxMeta?.unit || 'PZ'}</span>
                  </div>
                  {dxMeta?.price > 0 && (
                    <p className="text-sm text-gray-600 ml-auto">€ {dxMeta.price.toFixed(2)} / {dxMeta.unit || 'CAD'}</p>
                  )}
                </div>
              </div>

              {/* Tappo SX */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Consigliato</span>
                  <span className="font-medium">Tappo SX — <span className="font-mono text-sm text-gray-500">{tappi.sx}</span></span>
                </div>
                {sxMeta && <p className="text-sm text-gray-500 mb-2">{sxMeta.description}</p>}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Qtà:</label>
                    <input type="number" inputMode="numeric" min="0" value={tappiSxQty === 0 ? '' : tappiSxQty}
                      onChange={(e) => setTappiSxQty(e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-20 p-2 text-center text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
                    <span className="text-sm text-gray-500">{sxMeta?.unit || 'PZ'}</span>
                  </div>
                  {sxMeta?.price > 0 && (
                    <p className="text-sm text-gray-600 ml-auto">€ {sxMeta.price.toFixed(2)} / {sxMeta.unit || 'CAD'}</p>
                  )}
                </div>
              </div>

              {/* Antisfilamento */}
              <div className="bg-white border-2 border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold">Obbligatorio</span>
                  <span className="font-medium">Kit Antisfilamento — <span className="font-mono text-sm text-gray-500">{asCode}</span></span>
                </div>
                {asMeta && <p className="text-sm text-gray-500 mb-1">{asMeta.description}</p>}
                <p className="text-sm text-red-600 font-medium mb-3">Obbligatori per il montaggio della pensilina</p>

                {/* Reference table */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                    Schema Kit Antisfilamento — {penType === 'linea-mini' ? 'Linea Mini 3' : 'Linea Maxi 3'}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">Numero di accessori antisfilamento in base alla larghezza della lastra di vetro.</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-200">
                        <th className="px-3 py-1.5 text-left font-bold text-gray-700">Larghezza vetro</th>
                        <th className="px-3 py-1.5 text-center font-bold text-gray-700">N. Accessori</th>
                      </tr>
                    </thead>
                    <tbody>
                      {penType === 'linea-mini' ? (
                        <>
                          <tr className="border-b border-gray-200"><td className="px-3 py-1.5">fino a 3 m</td><td className="px-3 py-1.5 text-center font-semibold">2</td></tr>
                          <tr><td className="px-3 py-1.5">da 3 m fino a 4 m</td><td className="px-3 py-1.5 text-center font-semibold">3</td></tr>
                        </>
                      ) : (
                        <>
                          <tr className="border-b border-gray-200"><td className="px-3 py-1.5">fino a 1,5 m</td><td className="px-3 py-1.5 text-center font-semibold">2</td></tr>
                          <tr className="border-b border-gray-200"><td className="px-3 py-1.5">da 1,5 m fino a 2,5 m</td><td className="px-3 py-1.5 text-center font-semibold">3</td></tr>
                          <tr className="border-b border-gray-200"><td className="px-3 py-1.5">da 2,5 m fino a 3 m</td><td className="px-3 py-1.5 text-center font-semibold">4</td></tr>
                          <tr><td className="px-3 py-1.5">da 3 m fino a 4 m</td><td className="px-3 py-1.5 text-center font-semibold">5</td></tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-gray-500 mb-3">Consulta la tabella sopra e inserisci la quantità in base alla larghezza della tua lastra di vetro.</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Qtà:</label>
                    <input type="number" inputMode="numeric" min="1" value={antisfilQty === 0 ? '' : antisfilQty}
                      onChange={(e) => setAntisfilQty(e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-20 p-2 text-center text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
                    <span className="text-sm text-gray-500">{asMeta?.unit || 'PZ'}</span>
                  </div>
                  {asMeta?.price > 0 && (
                    <p className="text-sm text-gray-600 ml-auto">€ {asMeta.price.toFixed(2)} / {asMeta.unit || 'CAD'}</p>
                  )}
                </div>
              </div>
            </div>
            )}

            <button onClick={() => {
              if (antisfilQty <= 0) {
                Swal.fire({ icon: 'warning', title: 'Antisfilamento obbligatorio', text: 'Il kit antisfilamento è obbligatorio per il montaggio. Inserisci una quantità.', confirmButtonColor: '#cd212a' });
                return;
              }
              handleAddKitToCart();
            }} disabled={accLoading} className="w-full py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium transition-colors disabled:opacity-50">
              Completa e aggiungi al carrello
            </button>
          </div>
          );
        })()}

        {/* ── Upload (progettazione flow — clone of Ninfa case 9) ── */}
        {step === 'upload' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-center mb-2">Fornitura con Progettazione</h2>
            <p className="text-gray-600 text-center mb-6">Aggiungi file del progetto per una valutazione accurata</p>
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="font-medium text-green-900">Formati supportati</h4>
                </div>
                <p className="text-sm text-green-800">JPG, JPEG, PNG, DWG, PDF, DXF, STL, OBJ - Massimo 10 file per progetto</p>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors">
                <input type="file" multiple accept=".jpg,.jpeg,.png,.dwg,.pdf,.dxf,.stl,.obj" className="hidden" id="pen-photo-upload"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const newFiles = Array.from(e.target.files);
                      if (photos.length + newFiles.length > 10) { alert('Massimo 10 file consentiti.'); return; }
                      setPhotos(prev => [...prev, ...newFiles]);
                    }
                  }} />
                <label htmlFor="pen-photo-upload" className="cursor-pointer">
                  <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="text-lg font-medium text-gray-900 mb-2">Aggiungi File</p>
                  <p className="text-sm text-gray-600 mb-2">Clicca per selezionare o trascina i file qui</p>
                  <p className="text-xs text-gray-500">Puoi scattare foto da mobile o caricare file esistenti</p>
                </label>
              </div>
              {photos.length > 0 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-900">File caricati ({photos.length}/10)</h4>
                    <button onClick={() => setPhotos([])} className="text-sm text-red-600 hover:text-red-800">Rimuovi tutti</button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((file, index) => (
                      <div key={index} className="relative group">
                        <img src={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-gray-200" />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg" />
                        <button onClick={() => setPhotos(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                          {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => setStep('supplyType')} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">Indietro</button>
                <button onClick={() => setStep('notes')} disabled={photos.length === 0}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Avanti</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Notes (progettazione flow — clone of Ninfa case 10) ── */}
        {step === 'notes' && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-center mb-2">Note Aggiuntive</h2>
            <p className="text-gray-600 text-center mb-6 text-sm">Aggiungi dettagli specifici per il tuo progetto personalizzato</p>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Metri indicativi</label>
                <input type="text" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Es. 12" value={progData.metriIndicativi} onChange={(e) => updateProgData({ metriIndicativi: e.target.value })} />
              </div>
              <div className="space-y-3">
                {[
                  { key: 'vuoiGiunzioni', label: 'Vuoi giunzioni?', detailKey: 'giunzioniDettaglio', needsDetail: true },
                  { key: 'vuoiCorrimano', label: 'Vuoi corrimano?', detailKey: 'corrimanoDettaglio', needsDetail: true },
                  { key: 'vuoiTappi', label: 'Vuoi tappi?', needsDetail: false },
                  { key: 'vuoiSpessori', label: 'Vuoi spessori?', needsDetail: false },
                  { key: 'vuoiCoprifilo', label: 'Vuoi coprifilo?', detailKey: 'coprifiloDettaglio', needsDetail: true },
                  { key: 'vuoiLed', label: 'Vuoi led?', needsDetail: false },
                ].map(({ key, label, detailKey, needsDetail }) => (
                  <div key={key}>
                    <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <input type="checkbox" className="w-5 h-5 text-red-500 rounded border-gray-300 focus:ring-red-500"
                        checked={progData[key] || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const updates = { [key]: checked };
                          if (!checked && detailKey) updates[detailKey] = '';
                          if (checked && needsDetail) setDetailModal({ open: true, field: detailKey, label });
                          updateProgData(updates);
                        }} />
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                      {progData[key] && detailKey && progData[detailKey] && (
                        <span className="ml-auto text-xs text-gray-500 truncate max-w-[150px] sm:max-w-[250px]">{progData[detailKey]}</span>
                      )}
                      {progData[key] && needsDetail && (
                        <button type="button" className="ml-auto text-xs text-red-500 underline"
                          onClick={(e) => { e.preventDefault(); setDetailModal({ open: true, field: detailKey, label }); }}>
                          {progData[detailKey] ? 'Modifica' : 'Specifica'}
                        </button>
                      )}
                    </label>
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrizione del progetto e specifiche tecniche</label>
                <textarea className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows="4" placeholder="Descrivi il progetto, specifiche tecniche, vincoli, tempistiche..."
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">Riepilogo progetto</h4>
                <div className="text-sm text-red-800 space-y-1">
                  <div className="flex justify-between"><span>Tipo fornitura:</span><span className="font-medium">Con Progettazione</span></div>
                  <div className="flex justify-between"><span>File caricati:</span><span className="font-medium">{photos.length} file</span></div>
                  {progData.metriIndicativi && <div className="flex justify-between"><span>Metri indicativi:</span><span className="font-medium">{progData.metriIndicativi}</span></div>}
                  {notes && notes.trim() && <div className="flex justify-between"><span>Descrizione:</span><span className="font-medium">Inclusa</span></div>}
                </div>
              </div>
              <div className="flex justify-between">
                <button onClick={() => setStep('upload')} className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">Indietro</button>
                <button onClick={handleAddProgToCart} className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">Completa Progetto</button>
              </div>
            </div>
            {/* Detail Modal */}
            {detailModal.open && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                  <h3 className="text-lg font-semibold mb-3">{detailModal.label}</h3>
                  <p className="text-sm text-gray-600 mb-4">Quale/i? Specifica i dettagli:</p>
                  <textarea className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows="4" placeholder="Scrivi qui i dettagli..." value={progData[detailModal.field] || ''}
                    onChange={(e) => updateProgData({ [detailModal.field]: e.target.value })} autoFocus />
                  <div className="flex justify-end gap-3 mt-4">
                    <button onClick={() => setDetailModal({ open: false, field: '', label: '' })} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annulla</button>
                    <button onClick={() => setDetailModal({ open: false, field: '', label: '' })} className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">Conferma</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <RALModal isOpen={showRalModal}
        onClose={() => { setShowRalModal(false); setPendingFinish(null); }}
        onConfirm={(formattedRal) => {
          setRalCode(formattedRal.replace('RAL ', '')); setFinish(pendingFinish);
          setShowRalModal(false); setPendingFinish(null); setStep('supplyType');
        }}
        finishType={pendingFinish} />
    </div>
  );
};

export default PensilineOrderForm;
